import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'app-theme-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly modeSubject = new BehaviorSubject<ThemeMode>(this.readStoredMode());
  private readonly resolvedSubject = new BehaviorSubject<ResolvedTheme>('light');
  private mediaQuery?: MediaQueryList;

  readonly mode$ = this.modeSubject.asObservable();
  readonly resolved$ = this.resolvedSubject.asObservable();

  constructor() {
    if (typeof window !== 'undefined') {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQuery.addEventListener('change', () => {
        if (this.modeSubject.value === 'system') {
          this.applyResolved();
        }
      });
    }
    this.applyResolved();
  }

  get mode(): ThemeMode {
    return this.modeSubject.value;
  }

  get resolved(): ResolvedTheme {
    return this.resolvedSubject.value;
  }

  setMode(mode: ThemeMode): void {
    this.modeSubject.next(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
    this.applyResolved();
  }

  /** Toggles light ↔ dark */
  toggle(): void {
    this.setMode(this.resolved === 'dark' ? 'light' : 'dark');
  }

  private readStoredMode(): ThemeMode {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      /* ignore */
    }
    return 'light';
  }

  private resolve(): ResolvedTheme {
    if (this.modeSubject.value === 'light') {
      return 'light';
    }
    if (this.modeSubject.value === 'dark') {
      return 'dark';
    }
    return this.mediaQuery?.matches ? 'dark' : 'light';
  }

  private applyResolved(): void {
    const resolved = this.resolve();
    this.resolvedSubject.next(resolved);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolved);
      document.body.classList.toggle('theme-dark', resolved === 'dark');
      document.body.classList.toggle('theme-light', resolved === 'light');
    }
  }
}
